/**
 * ORR-351 — Avisar a los responsables cuando releer el padrón de ARCA cambia la categoría
 * de monotributo de un cliente.
 *
 * Lo usan los dos puntos de escritura de ORR-233: el botón de la ficha
 * (`useSincronizarPadronCliente`) y la acción masiva (`useSincronizarPadronMasivo`). Con un
 * cambio manda el aviso individual con enlace a la ficha; con varios, UN resumen al listado,
 * porque con 68 monotributistas avisar de a uno inunda la campana.
 *
 * Vive fuera de los hooks para poder probarse sin montar React, igual que
 * `ejecutarEnvioRecibo` en ORR-354.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { notificacionesService } from '@/core/services/notificacionesService'
import { rutaFichaCliente } from '@/lib/ruta-cliente'
import { PLANTILLAS_NOTIFICACION } from '@/core/types/notificaciones'
import type {
  DestinatarioSpec,
  NotificacionOrigen,
  TipoNotificacion,
} from '@/core/types/notificaciones'

export interface CambioCategoria {
  clienteId: string
  nombre: string
  /** NULL = el cliente no tenía categoría cargada. */
  anterior: string | null
  nueva: string
}

export interface AvisoCambioCategoria {
  tipo: TipoNotificacion
  titulo: string
  mensaje: string
  enlace: string
  destinatarios: DestinatarioSpec[]
  /** Solo en el aviso individual: el cliente, para `origen.registro_id`. */
  registroId?: string
}

type Notificar = typeof notificacionesService.notificar

/**
 * Pura. Con un cambio, aviso individual con enlace a la ficha; con varios, resumen al listado.
 * Sin cambios no hay aviso.
 */
export const armarAvisoCambioCategoria = (
  cambios: CambioCategoria[],
  actor: string,
): AvisoCambioCategoria | null => {
  if (cambios.length === 0) return null

  // Un spec por cliente: el resolver deduplica emails, así que un responsable de varios
  // clientes recibe UNA fila, no una por cliente.
  const destinatarios: DestinatarioSpec[] = [
    ...new Set(cambios.map((c) => `responsables:${c.clienteId}`)),
  ]

  if (cambios.length === 1) {
    const [c] = cambios
    return {
      ...PLANTILLAS_NOTIFICACION.categoriaMonotributoCambiada(c.nombre, c.anterior, c.nueva, actor),
      enlace: rutaFichaCliente(c.clienteId),
      destinatarios,
      registroId: c.clienteId,
    }
  }

  return {
    ...PLANTILLAS_NOTIFICACION.categoriasMonotributoCambiadasLote(cambios, actor),
    enlace: '/clientes',
    destinatarios,
  }
}

/**
 * Avisa a los responsables de los clientes cuya categoría cambió. **Nunca lanza.**
 *
 * Corre cuando la faceta YA está escrita: una excepción acá convertiría una sincronización
 * exitosa en un error de pantalla. El actor se excluye y se valida contra el JWT adentro de
 * `notificar()`; sin email en sesión no hay a quién atribuir el cambio y no se avisa.
 *
 * `notificar` es inyectable para los tests; en producción es el servicio canónico.
 */
export const notificarCambiosCategoria = async (
  client: SupabaseClient<Database>,
  actorEmail: string | undefined,
  cambios: CambioCategoria[],
  actorNombre?: string,
  notificar: Notificar = (c, input) => notificacionesService.notificar(c, input),
): Promise<void> => {
  if (!actorEmail) return
  const aviso = armarAvisoCambioCategoria(cambios, actorNombre || actorEmail)
  if (!aviso) return

  const origen: NotificacionOrigen = {
    canal: 'web',
    actor_email: actorEmail,
    tabla_fuente: 'cliente_faceta_contable',
    registro_id: aviso.registroId,
  }

  try {
    await notificar(client, {
      destinatarios: aviso.destinatarios,
      titulo: aviso.titulo,
      mensaje: aviso.mensaje,
      tipo: aviso.tipo,
      enlace: aviso.enlace,
      origen,
    })
  } catch (err) {
    console.error('Error notificando el cambio de categoría:', err)
  }
}
